import { useState, useEffect } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AppProvider, useApp } from '@/context/AppContext'
import { Capacitor } from '@capacitor/core'
import { Keyboard } from '@capacitor/keyboard'
import { usePushNotifications } from '@/hooks/usePushNotifications'
import { useTheme } from '@/hooks/useTheme'
import Landing      from '@/screens/Landing'
import Login        from '@/screens/Login'
import Register     from '@/screens/Register'
import ForgotPassword from '@/screens/ForgotPassword'
import Onboarding   from '@/screens/Onboarding'
import Dashboard    from '@/screens/Dashboard'
import Chat         from '@/screens/Chat'
import Documents    from '@/screens/Documents'
import CpsTracker   from '@/screens/CpsTracker'
import CommsLog     from '@/screens/CommsLog'
import EventLog     from '@/screens/EventLog'
import CaseAnalyst  from '@/screens/CaseAnalyst'
import Calendar     from '@/screens/Calendar'
import Children     from '@/screens/Children'
import Orders       from '@/screens/Orders'
import CoParent     from '@/screens/CoParent'
import Settings        from '@/screens/Settings'
import Messages        from '@/screens/Messages'
import DirectMessages  from '@/screens/DirectMessages'
import ExpenseLog      from '@/screens/ExpenseLog'
import VerifyEmail     from '@/screens/VerifyEmail'
import Verify2FA       from '@/screens/Verify2FA'
import Coach           from '@/screens/Coach'
import PrivacyPolicy   from '@/screens/PrivacyPolicy'
import TermsOfService  from '@/screens/TermsOfService'
import AskAnything    from '@/screens/AskAnything'
import AttorneyMatch  from '@/screens/AttorneyMatch'
import CreditScore    from '@/screens/CreditScore'
import CourtReport    from '@/screens/CourtReport'
import ParentingTime  from '@/screens/ParentingTime'
import ChildSupport   from '@/screens/ChildSupport'
import WellbeingLog      from '@/screens/WellbeingLog'
import ViolationTracker  from '@/screens/ViolationTracker'
import CaseTimeline      from '@/screens/CaseTimeline'
import MedicalRecords    from '@/screens/MedicalRecords'
import ExpenseAnalytics  from '@/screens/ExpenseAnalytics'
import Import            from '@/screens/Import'
import BottomNav          from '@/components/layout/BottomNav'

const APP_SCREENS = [
  '/dashboard', '/chat', '/documents', '/cps', '/comms', '/events',
  '/case-analyst', '/calendar', '/children', '/orders', '/coparent',
  '/settings', '/messages', '/direct-messages', '/expenses', '/coach',
  '/ask', '/attorney-match', '/credit', '/court-report', '/parenting-time',
  '/child-support', '/wellbeing', '/violations', '/timeline', '/medical',
  '/expense-analytics',
  '/import',
]

// ── Auth guard: must be logged in ─────────────────────────────────────────────
function AuthGuard({ children }) {
  const { user, authLoading } = useApp()
  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-full bg-white dark:bg-slate-900">
        <div className="flex flex-col items-center gap-4">
          <span className="text-4xl animate-bounce">🛡️</span>
          <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">Loading GoodGuardian…</p>
        </div>
      </div>
    )
  }
  if (!user) return <Navigate to="/login" replace />
  return children
}

// ── Onboarding guard: must be onboarded ───────────────────────────────────────
function Guard({ children }) {
  const { isOnboarded } = useApp()
  return isOnboarded ? children : <Navigate to="/onboarding" replace />
}

// ── Redirect if already logged in ─────────────────────────────────────────────
function PublicOnly({ children }) {
  const { user, authLoading } = useApp()
  if (authLoading) return null
  if (user) return <Navigate to="/dashboard" replace />
  return children
}

function AppShell() {
  const location = useLocation()
  const showNav  = APP_SCREENS.some(s => location.pathname.startsWith(s))
  const isForgotPassword = location.pathname.startsWith('/forgot-password')
  const [kbHeight, setKbHeight] = useState(0)

  // Apply dark/light class to <html> — must be called inside AppProvider
  // so Settings can call useTheme() and read/write the same preference.
  useTheme()

  // Register for iOS push notifications (no-op on web)
  usePushNotifications()

  // ── Global keyboard avoidance — shrinks the app shell by the keyboard height
  // so every screen's bottom composer is visible without per-screen patches.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return
    let showListener, hideListener
    const attach = async () => {
      showListener = await Keyboard.addListener('keyboardWillShow', ({ keyboardHeight }) => {
        setKbHeight(keyboardHeight)
      })
      hideListener = await Keyboard.addListener('keyboardWillHide', () => {
        setKbHeight(0)
      })
    }
    attach()
    return () => { showListener?.remove(); hideListener?.remove() }
  }, [])

  const shellStyle = !isForgotPassword && kbHeight
    ? { height: `calc(100dvh - ${kbHeight}px)` }
    : undefined

  return (
    <div
      className={isForgotPassword ? 'h-full bg-slate-100 overflow-auto' : 'app-shell'}
      style={shellStyle}
    >
      <a
        href="#app-main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:top-2 focus:left-2 focus:bg-white focus:text-brand-700 focus:px-3 focus:py-2 focus:rounded-lg focus:shadow"
      >
        Skip to main content
      </a>

      <main id="app-main" className="flex-1 min-h-0 overflow-hidden flex flex-col">
      <Routes>
        {/* Public */}
        <Route path="/"             element={<Landing />} />
        <Route path="/login"        element={<PublicOnly><Login /></PublicOnly>} />
        <Route path="/register"     element={<PublicOnly><Register /></PublicOnly>} />
        <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/verify-2fa"   element={<Verify2FA />} />
        <Route path="/privacy"      element={<PrivacyPolicy />} />
        <Route path="/terms"        element={<TermsOfService />} />
        <Route path="/onboarding"   element={<AuthGuard><Onboarding /></AuthGuard>} />

        {/* App — require login + onboarding */}
        <Route path="/dashboard"   element={<AuthGuard><Guard><Dashboard /></Guard></AuthGuard>} />
        <Route path="/chat"        element={<AuthGuard><Guard><Chat /></Guard></AuthGuard>} />
        <Route path="/documents"   element={<AuthGuard><Guard><Documents /></Guard></AuthGuard>} />
        <Route path="/cps"         element={<AuthGuard><Guard><CpsTracker /></Guard></AuthGuard>} />
        <Route path="/comms"       element={<AuthGuard><Guard><CommsLog /></Guard></AuthGuard>} />
        <Route path="/events"      element={<AuthGuard><Guard><EventLog /></Guard></AuthGuard>} />
        <Route path="/case-analyst" element={<AuthGuard><Guard><CaseAnalyst /></Guard></AuthGuard>} />
        <Route path="/calendar"    element={<AuthGuard><Guard><Calendar /></Guard></AuthGuard>} />
        <Route path="/children"    element={<AuthGuard><Guard><Children /></Guard></AuthGuard>} />
        <Route path="/orders"      element={<AuthGuard><Guard><Orders /></Guard></AuthGuard>} />
        <Route path="/coparent"    element={<AuthGuard><Guard><CoParent /></Guard></AuthGuard>} />
        <Route path="/settings"         element={<AuthGuard><Guard><Settings /></Guard></AuthGuard>} />
        <Route path="/messages"         element={<AuthGuard><Guard><Messages /></Guard></AuthGuard>} />
        <Route path="/direct-messages"  element={<AuthGuard><Guard><DirectMessages /></Guard></AuthGuard>} />
        <Route path="/expenses"         element={<AuthGuard><Guard><ExpenseLog /></Guard></AuthGuard>} />
        <Route path="/coach"            element={<AuthGuard><Guard><Coach /></Guard></AuthGuard>} />
        <Route path="/ask"             element={<AuthGuard><Guard><AskAnything /></Guard></AuthGuard>} />
        <Route path="/attorney-match"  element={<AuthGuard><Guard><AttorneyMatch /></Guard></AuthGuard>} />
        <Route path="/credit"          element={<AuthGuard><Guard><CreditScore /></Guard></AuthGuard>} />
        <Route path="/court-report"    element={<AuthGuard><Guard><CourtReport /></Guard></AuthGuard>} />
        <Route path="/parenting-time"  element={<AuthGuard><Guard><ParentingTime /></Guard></AuthGuard>} />
        <Route path="/child-support"   element={<AuthGuard><Guard><ChildSupport /></Guard></AuthGuard>} />
        <Route path="/wellbeing"       element={<AuthGuard><Guard><WellbeingLog /></Guard></AuthGuard>} />
        <Route path="/violations"      element={<AuthGuard><Guard><ViolationTracker /></Guard></AuthGuard>} />
        <Route path="/timeline"        element={<AuthGuard><Guard><CaseTimeline /></Guard></AuthGuard>} />
        <Route path="/medical"          element={<AuthGuard><Guard><MedicalRecords /></Guard></AuthGuard>} />
        <Route path="/expense-analytics" element={<AuthGuard><Guard><ExpenseAnalytics /></Guard></AuthGuard>} />
        <Route path="/import"            element={<AuthGuard><Guard><Import /></Guard></AuthGuard>} />

        {/* Deep-link aliases */}
        <Route path="/court-orders"  element={<AuthGuard><Guard><Navigate to="/orders" replace /></Guard></AuthGuard>} />
        <Route path="/event-log"     element={<AuthGuard><Guard><Navigate to="/events" replace /></Guard></AuthGuard>} />
        <Route path="/notifications" element={<AuthGuard><Guard><Navigate to="/dashboard" replace /></Guard></AuthGuard>} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </main>

      {showNav && <BottomNav />}
    </div>
  )
}

export default function App() {
  return (
    <AppProvider>
      <AppShell />
    </AppProvider>
  )
}
